import Cookies from 'js-cookie';
import { createReducer } from 'reduxsauce';
import { cloneDeep, isEqual } from 'lodash';

import storage from '../../helpers/storage';
import { FETCH_SESSION, RESET_SYSTEM_SCOPE } from '../../modules/system/redux/types';
import { AuthTypes } from '../actions/auth.actions';

// eslint-disable-next-line  @typescript-eslint/no-explicit-any
const initialState: any = {
  status: [],
  user: storage.get('user') ? JSON.parse(storage.get('user') || '') : null,
  token: Cookies.get('access_token') || null,
  deviceToken: storage.get('device_token') || null,
  timezone: storage.get('timezone') || null,
  subscription: storage.get('subscription') ? JSON.parse(storage.get('subscription') || '') : null,
  // True while `user` was derived from `fetchSession` rather than set by a legacy login. Such a
  // user is kept in memory only and is never written to `localStorage`.
  isSessionDerivedUser: false,
  latestFetchSessionRequestId: null,
};

/**
 * Legacy login stores the backend's login-response `user` here, and ~77 call sites (including
 * `usePermissionsManager`) still read `state.auth.user`. An SSO login never goes through that path,
 * so this bridges the `fetchSession` result into the same shape instead: the session user with the
 * session company merged in as `user.company`, matching `generateLoginOutput`'s legacy shape.
 * A legacy-login user is never overwritten; stale `fetchSession` results are ignored the same way
 * `modules/system/redux/reducers.ts` ignores them.
 */
/** The parts of the legacy auth state the session-user bridge reads and writes. */
interface SessionUserBridgeState {
  readonly user: Record<string, unknown> | null;
  readonly isSessionDerivedUser: boolean;
  readonly latestFetchSessionRequestId: string | null;
}

/** The `fetchSession` thunk lifecycle actions this bridge consumes. */
interface FetchSessionLifecycleAction {
  readonly meta?: { readonly requestId?: string };
  readonly payload?: {
    readonly session?: {
      readonly user?: Record<string, unknown> | null;
      readonly company?: Record<string, unknown> | null;
    } | null;
  };
}

const fetchSessionPending = <S extends SessionUserBridgeState>(
  state: S,
  action: FetchSessionLifecycleAction,
): S => ({
  ...state,
  latestFetchSessionRequestId: action.meta?.requestId ?? null,
});

const fetchSessionFulfilled = <S extends SessionUserBridgeState>(
  state: S,
  action: FetchSessionLifecycleAction,
): S => {
  if (action.meta?.requestId !== state.latestFetchSessionRequestId) {
    return state;
  }
  if (state.user && !state.isSessionDerivedUser) {
    return state;
  }
  const session = action.payload?.session;
  if (!session?.user) {
    return state.isSessionDerivedUser ? { ...state, user: null, isSessionDerivedUser: false } : state;
  }
  return {
    ...state,
    user: { ...session.user, company: session.company ? { ...session.company } : null },
    isSessionDerivedUser: true,
  };
};

// Also forgets the in-flight `fetchSession`, so a check started before the reset cannot bridge the
// previous identity back in.
const resetSystemScope = <S extends SessionUserBridgeState>(state: S): S =>
  state.isSessionDerivedUser
    ? { ...state, user: null, isSessionDerivedUser: false, latestFetchSessionRequestId: null }
    : { ...state, latestFetchSessionRequestId: null };

// eslint-disable-next-line  @typescript-eslint/no-explicit-any
const loginRequest = (state: any) => ({ ...state, status: ['pending-login'] });
// eslint-disable-next-line  @typescript-eslint/no-explicit-any
const loginSuccess = (state: any, action: any) => ({
  ...state,
  status: [],
  user: action.response.user,
  isSessionDerivedUser: false,
  token: action.response.token,
  deviceToken: action.response.deviceToken,
  timezone: action.response.timezone,
  subscription: action.response.subscription,
});
// eslint-disable-next-line  @typescript-eslint/no-explicit-any
const loginFailure = (state: any) => ({ ...state, status: ['failed-login'] });

// eslint-disable-next-line  @typescript-eslint/no-explicit-any
const registerDeviceTokenRequest = (state: any) => ({
  ...state,
  status: ['register-device-token'],
});
// eslint-disable-next-line  @typescript-eslint/no-explicit-any
const registerDeviceTokenSuccess = (state: any, action: any) => {
  const { deviceToken } = action;
  return { ...state, deviceToken, status: [] };
};
// eslint-disable-next-line  @typescript-eslint/no-explicit-any
const registerDeviceTokenFailure = (state: any) => ({
  ...state,
  deviceToken: null,
  status: [],
});

// eslint-disable-next-line  @typescript-eslint/no-explicit-any
const logoutRequest = (state: any) => ({
  ...state,
  status: ['pending-logout'],
});
// eslint-disable-next-line  @typescript-eslint/no-explicit-any
const logoutSuccess = (state: any) => ({ ...state, status: [] });
// eslint-disable-next-line  @typescript-eslint/no-explicit-any
const logoutFailure = (state: any) => ({ ...state, status: [] });

// eslint-disable-next-line  @typescript-eslint/no-explicit-any
const forgotPasswordRequest = (state: any) => ({
  ...state,
  status: ['pending-forgotpassword'],
});

// eslint-disable-next-line  @typescript-eslint/no-explicit-any
const forgotPasswordSuccess = (state: any) => ({ ...state, status: [] });
// eslint-disable-next-line  @typescript-eslint/no-explicit-any
const forgotPasswordFailure = (state: any) => ({ ...state, status: [] });

// eslint-disable-next-line  @typescript-eslint/no-explicit-any
const resetPasswordRequest = (state: any) => ({
  ...state,
  status: ['pending-resetpassword'],
  passwordError: null,
});

// eslint-disable-next-line  @typescript-eslint/no-explicit-any
const resetPasswordSuccess = (state: any) => ({ ...state, status: [], passwordError: null });
// eslint-disable-next-line  @typescript-eslint/no-explicit-any
const resetPasswordFailure = (state: any, action: any) => ({
  ...state,
  status: [],
  passwordError: action.message || null,
});

// eslint-disable-next-line  @typescript-eslint/no-explicit-any
const updateUserInformationRequest = (state: any) => ({
  ...state,
  status: ['pending-updateuserinformation'],
});

// eslint-disable-next-line  @typescript-eslint/no-explicit-any
const updateUserInformationSuccess = (state: any, action: any) => {
  let user = cloneDeep(state.user);
  if (isEqual(user.id, action.response.id)) {
    user = { ...user, ...action.response };
    if (!state.isSessionDerivedUser) {
      try {
        storage.set('user', JSON.stringify(user));
      } catch {
        // Ignore storage write failures (e.g. QuotaExceededError) so profile update does not crash
      }
    }
  }
  return { ...state, user, status: [] };
};
// eslint-disable-next-line  @typescript-eslint/no-explicit-any
const updateUserInformationFailure = (state: any) => ({ ...state, status: [] });

// eslint-disable-next-line  @typescript-eslint/no-explicit-any
const setUpdatedUser = (state: any, action: any) => {
  const user = cloneDeep(state.user);
  if (isEqual(user.id, action.payload.id)) {
    const updatedUser = { ...user, ...action.payload, company: user.company };
    if (!state.isSessionDerivedUser) {
      storage.set('user', JSON.stringify(updatedUser));
    }
    return { ...state, user: updatedUser };
  }
  return { ...state, user };
};

// eslint-disable-next-line  @typescript-eslint/no-explicit-any
const getMyCompanyRequest = (state: any) => ({
  ...state,
  status: ['pending-get-my-company'],
});

// eslint-disable-next-line  @typescript-eslint/no-explicit-any
const getMyCompanySuccess = (state: any, action: any) => {
  const updatedUser = {
    ...state.user,
    company: { ...action.response.company },
  };
  if (!state.isSessionDerivedUser) {
    storage.set('user', JSON.stringify(updatedUser));
  }
  return {
    ...state,
    status: [],
    user: updatedUser,
  };
};

// eslint-disable-next-line  @typescript-eslint/no-explicit-any
const getMyCompanyFailure = (state: any) => ({ ...state, status: [] });

// eslint-disable-next-line  @typescript-eslint/no-explicit-any
const updatePasswordRequest = (state: any) => ({
  ...state,
  status: ['pending-updatepassword'],
});

// eslint-disable-next-line  @typescript-eslint/no-explicit-any
const updatePasswordSuccess = (state: any) => ({ ...state, status: [] });

// eslint-disable-next-line  @typescript-eslint/no-explicit-any
const updatePasswordFailure = (state: any) => ({ ...state, status: [] });

// eslint-disable-next-line  @typescript-eslint/no-explicit-any
const acceptInviteRequest = (state: any) => ({
  ...state,
  status: ['pending-acceptinvite'],
});

// eslint-disable-next-line  @typescript-eslint/no-explicit-any
const acceptInviteSuccess = (state: any, action: any) => ({
  ...state,
  status: [],
  user: action.response,
  isSessionDerivedUser: false,
});

// eslint-disable-next-line  @typescript-eslint/no-explicit-any
const acceptInviteFailure = (state: any, action: any) => ({
  ...state,
  inviteError: { ...action },
});

// eslint-disable-next-line  @typescript-eslint/no-explicit-any
const setPasswordRequest = (state: any) => ({
  ...state,
  status: ['pending-setpassword'],
  passwordError: null,
});

// eslint-disable-next-line  @typescript-eslint/no-explicit-any
const setPasswordSuccess = (state: any) => ({
  ...state,
  status: [],
  passwordError: null,
  user: null,
  token: null,
  deviceToken: null,
});

// eslint-disable-next-line  @typescript-eslint/no-explicit-any
const setPasswordFailure = (state: any, action: any) => ({
  ...state,
  status: [],
  passwordError: action.message || null,
});

// eslint-disable-next-line  @typescript-eslint/no-explicit-any
const clearRequest = (state: any) => ({ ...state, status: [] });

// eslint-disable-next-line  @typescript-eslint/no-explicit-any
const clearAuthRequest = (state: any) => ({
  ...state,
  status: [],
  user: null,
  token: null,
  deviceToken: null,
  isSessionDerivedUser: false,
});

export const reducer = createReducer(initialState, {
  [AuthTypes.LOGIN_REQUEST]: loginRequest,
  [AuthTypes.LOGIN_SUCCESS]: loginSuccess,
  [AuthTypes.LOGIN_FAILURE]: loginFailure,

  [AuthTypes.REGISTER_DEVICE_TOKEN_REQUEST]: registerDeviceTokenRequest,
  [AuthTypes.REGISTER_DEVICE_TOKEN_SUCCESS]: registerDeviceTokenSuccess,
  [AuthTypes.REGISTER_DEVICE_TOKEN_FAILURE]: registerDeviceTokenFailure,

  [AuthTypes.LOGOUT_REQUEST]: logoutRequest,
  [AuthTypes.LOGOUT_SUCCESS]: logoutSuccess,
  [AuthTypes.LOGOUT_FAILURE]: logoutFailure,

  [AuthTypes.FORGOT_PASSWORD_REQUEST]: forgotPasswordRequest,
  [AuthTypes.FORGOT_PASSWORD_SUCCESS]: forgotPasswordSuccess,
  [AuthTypes.FORGOT_PASSWORD_FAILURE]: forgotPasswordFailure,

  [AuthTypes.RESET_PASSWORD_REQUEST]: resetPasswordRequest,
  [AuthTypes.RESET_PASSWORD_SUCCESS]: resetPasswordSuccess,
  [AuthTypes.RESET_PASSWORD_FAILURE]: resetPasswordFailure,

  [AuthTypes.UPDATE_USER_INFORMATION_REQUEST]: updateUserInformationRequest,
  [AuthTypes.UPDATE_USER_INFORMATION_SUCCESS]: updateUserInformationSuccess,
  [AuthTypes.UPDATE_USER_INFORMATION_FAILURE]: updateUserInformationFailure,

  [AuthTypes.SET_UPDATED_USER]: setUpdatedUser,

  [AuthTypes.GET_MY_COMPANY_REQUEST]: getMyCompanyRequest,
  [AuthTypes.GET_MY_COMPANY_SUCCESS]: getMyCompanySuccess,
  [AuthTypes.GET_MY_COMPANY_FAILURE]: getMyCompanyFailure,

  [AuthTypes.UPDATE_PASSWORD_REQUEST]: updatePasswordRequest,
  [AuthTypes.UPDATE_PASSWORD_SUCCESS]: updatePasswordSuccess,
  [AuthTypes.UPDATE_PASSWORD_FAILURE]: updatePasswordFailure,

  [AuthTypes.ACCEPT_INVITE_REQUEST]: acceptInviteRequest,
  [AuthTypes.ACCEPT_INVITE_SUCCESS]: acceptInviteSuccess,
  [AuthTypes.ACCEPT_INVITE_FAILURE]: acceptInviteFailure,

  [AuthTypes.SET_PASSWORD_REQUEST]: setPasswordRequest,
  [AuthTypes.SET_PASSWORD_SUCCESS]: setPasswordSuccess,
  [AuthTypes.SET_PASSWORD_FAILURE]: setPasswordFailure,

  [AuthTypes.CLEAR_REQUEST]: clearRequest,
  [AuthTypes.CLEAR_AUTH_REQUEST]: clearAuthRequest,

  [`${FETCH_SESSION}/pending`]: fetchSessionPending,
  [`${FETCH_SESSION}/fulfilled`]: fetchSessionFulfilled,
  [RESET_SYSTEM_SCOPE]: resetSystemScope,
});
